import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { QRCodeSVG } from 'qrcode.react';
import Footer from "./partials/Footer";

const LOGO_SITO_URL = "https://www.ilnecrologio.info/img/ilNecrologio_info.svg";

function Necrologio() {
  const url = window.location.href;
  const { id: annuncioId } = useParams();
  const qrCodeRef = useRef();
  const [annuncio, setAnnuncio] = useState(null);
  const [allegati, setAllegati] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoadingAllegati, setIsLoadingAllegati] = useState(false);
  const [agenzia, setAgenzia] = useState(null);
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  
  const MYURL = typeof window !== 'undefined' ? window.location.href : "https://app.ilnecrologio.info/";
  
  // Funzione per costruire URL assoluto dell'immagine
  const getImageUrl = (annuncio) => {
    if (!annuncio || !annuncio.img) return LOGO_SITO_URL;
    
    // Costruisci URL assoluto per l'immagine
    const baseUrl = window.location.origin;
    return `${baseUrl}/necrologi/${annuncio.id_utente}/${annuncio.img}`;
  };

  // Funzione per verificare se l'immagine è accessibile
  const checkImageAccessibility = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, { 
        method: 'HEAD',
        credentials: 'omit' // Non inviare credenziali per il test
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  const titoloPerCondivisione = annuncio ? `In ricordo di ${annuncio.nome} ${annuncio.cognome}` : "Necrologio";

  // Titolo e descrizione dinamici per i meta tag
  const titoloDinamico = annuncio
    ? `In ricordo di ${annuncio.nome} ${annuncio.cognome} | ilNecrologio.info`
    : "ilNecrologio.info";

  const descrizioneDinamica = annuncio
    ? `Annuncio funebre per ${annuncio.nome} ${annuncio.cognome}. ${annuncio.testo ? annuncio.testo.substring(0, 150) + '...' : 'Consulta i dettagli.'}`
    : "Consulta i dettagli di questo annuncio funebre.";
    
  // Immagine dinamica con URL assoluto
  const [immagineDinamica, setImmagineDinamica] = useState(LOGO_SITO_URL);

  // Effetto per aggiornare l'immagine dinamica quando l'annuncio è disponibile
  useEffect(() => {
    if (annuncio) {
      const imageUrl = getImageUrl(annuncio);
      
      // Verifica se l'immagine è accessibile
      checkImageAccessibility(imageUrl).then(isAccessible => {
        if (isAccessible) {
          setImmagineDinamica(imageUrl);
        } else {
          // Se l'immagine non è accessibile, usa il logo
          setImmagineDinamica(LOGO_SITO_URL);
        }
      });
    }
  }, [annuncio]);

  useEffect(() => {
    if (annuncio) {
      setLikes(annuncio.cuori || 0);
    }
  }, [annuncio]);

  const handleLike = async () => {
    if (hasLiked) return;

    try {
      const response = await fetch(`/api/like/${annuncioId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        setLikes((prevLikes) => prevLikes + 1);
        setHasLiked(true);
      } else {
        console.error("Errore durante il like");
      }
    } catch (err) {
      console.error("Errore durante la richiesta:", err);
    }
  };

  useEffect(() => {
    console.log('annuncioId in useEffect:', annuncioId);

    const fetchAnnuncio = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/api/outNecrologio/${annuncioId}`, {
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          console.log("Dati ricevuti dall'API:", data);
          setAnnuncio(data[0]);
        } else {
          throw new Error("Errore nel recupero dell'annuncio. Stato HTTP: " + response.status);
        }
      } catch (err) {
        console.error("Errore durante il fetch:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnnuncio();
  }, [annuncioId]);
  
  useEffect(() => {
    if (annuncio && annuncio.id_utente) { 
      fetch(`/api/agof/${annuncio.id_utente}`)
        .then(response => response.json())
        .then(data => setAgenzia(data));
    }
  }, [annuncio]); 

  const fetchAllegati = async () => { 
    try {
      const response = await fetch(
        `/api/outAllegati/${annuncioId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAllegati(data || []);
      } else {
        if (response.status === 404) {
          setAllegati([]);
          console.log(`Nessun allegato trovato per annuncio ${annuncioId} (API ha risposto con 404).`);
        } else {
          console.error(
            `Errore HTTP ${response.status} nel recupero degli allegati per annuncio ${annuncioId}.`
          );
          setAllegati([]);
        }
      }
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchAllegati();
  }, [annuncioId]);

  const downloadQRCode = () => {
    const svg = qrCodeRef.current.querySelector("svg");
    const boundingBox = svg.getBBox();
    svg.setAttribute("width", boundingBox.width);
    svg.setAttribute("height", boundingBox.height);
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const qrSize = 1000;
    const borderSize = 12;
    const outputSize = qrSize + borderSize * 2;
    const targetColor = { r: 177, g: 157, b: 201 };
    const newColor = { r: 0, g: 0, b: 0 };

    const img = new Image();
    img.onload = () => {
      canvas.width = outputSize;
      canvas.height = outputSize;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, outputSize, outputSize);
      ctx.drawImage(img, borderSize, borderSize, qrSize, qrSize);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        if (
          data[i] === targetColor.r &&
          data[i + 1] === targetColor.g &&
          data[i + 2] === targetColor.b
        ) {
          data[i] = newColor.r;
          data[i + 1] = newColor.g;
          data[i + 2] = newColor.b;
        }
      }

      ctx.putImageData(imageData, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.href = pngFile;
      downloadLink.download = `QRCode_${annuncio.nome}_${annuncio.cognome}.png`;
      downloadLink.click();
    };

    img.src = `data:image/svg+xml;base64,${btoa(svgData)}`;
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>{titoloDinamico}</title>
        <meta name="description" content={descrizioneDinamica} />
        
        {/* Open Graph meta tags */}
        <meta property="og:title" content={titoloDinamico} />
        <meta property="og:description" content={descrizioneDinamica} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={immagineDinamica} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Foto di ${annuncio?.nome} ${annuncio?.cognome}`} />
        <meta property="og:site_name" content="ilNecrologio.info" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={titoloDinamico} /> 
        <meta name="twitter:description" content={descrizioneDinamica} />
        <meta name="twitter:image" content={immagineDinamica} />
        <meta name="twitter:image:alt" content={`Foto di ${annuncio?.nome} ${annuncio?.cognome}`} />
        
        {/* Facebook specific */}
        <meta property="fb:app_id" content="YOUR_FB_APP_ID" />
        
        {/* LinkedIn specific */}
        <meta property="article:author" content="ilNecrologio.info" />
      </Helmet>
      
      <section className="main_content dashboard_part">
        {isLoading ? (
          <p>Caricamento in corso...</p>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : annuncio ? ( 
          <React.Fragment key={annuncio._id}>
            <div className="row justify-content-center">
              <div className="col-md-8 col-sm-12 p-0">
                <div className="d-flex justify-content-center align-items-center p_30">
                  <div className="logo" style={{ width: "180px" }}>
                    <a href="https://www.ilnecrologio.info">
                      <img
                        src="/img/ilNecrologio_info.svg"
                        alt="ilNecrologio.info"
                        style={{ width: "150px" }}
                      />
                    </a>
                  </div>                  
                </div>
                <div className="d-flex justify-content-center align-items-center p_0">
                  <a href="#!" onClick={(e) => { e.preventDefault(); window.history.back(); }} className="btn btn-light">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="auto" viewBox="0 0 512 512">
                    <path fill="#B197FC" d="M48 256a208 208 0 1 1 416 0A208 208 0 1 1 48 256zm464 0A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM217.4 376.9c4.2 4.5 10.1 7.1 16.3 7.1c12.3 0 22.3-10 22.3-22.3l0-57.7 96 0c17.7 0 32-14.3 32-32l0-32c0-17.7-14.3-32-32-32l-96 0 0-57.7c0-12.3-10-22.3-22.3-22.3c-6.2 0-12.1 2.6-16.3 7.1L117.5 242.2c-3.5 3.8-5.5 8.7-5.5 13.8s2 10.1 5.5 13.8l99.9 107.1z"/></svg>
                    {" "}Indietro
                  </a>
                </div>
              </div>
            </div>

            <div className="col-md-8 offset-md-2 col-sm-12 p-0">
              <div className="header_iner justify-content-center">
                <div className="row justify-content-center">
                  <div className="col-md-8 col-sm-12 mt_50 mb_30">
                    <div className="white_box_tittle list_header">
                      <img
                        src={getImageUrl(annuncio)}
                        alt={`${annuncio.nome} ${annuncio.cognome}`}
                        onError={(e) => {
                          e.target.src = "/necrologi/vuoto.jpg";
                        }}
                        className="img-fluid"
                      />
                    </div>
                  </div>
                </div>
                <div className="serach_field-area text-center"> 
                  <h2 className="titoloPubblico mb_30">
                    {annuncio.nome} {annuncio.cognome}<hr />                    
                  </h2>
                  <h3 className="indirizzoPubblico" style={{fontWeight: '400'}}>
                    {annuncio.age && ` di anni ${annuncio.age}`}
                    {annuncio.nato && (
                      <>
                        <br /><br />
                        Nata/o a {annuncio.natoA} il{" "}
                        {new Date(annuncio.nato).toLocaleDateString("it-IT")}
                      </>
                    )}
                    {annuncio.decesso && (
                      <>
                        <br /><br />
                        Deceduta/o il{" "}{new Date(annuncio.decesso).toLocaleDateString("it-IT")}{" "}
                        a {annuncio.natoA}                        
                      </>
                    )}                    
                    {annuncio.coniuge && <>
                      <br /><br />
                      Coniuge: {annuncio.coniuge}
                    </>}
                    <br /><br />{annuncio.cap} - {annuncio.comune}{" "}({annuncio.prov}){" "}-{" "}{annuncio.regione}
                    <br /><br />
                    <div className="like-container text-center">
                      <button
                        className="btn btn-primary"
                        onClick={handleLike}
                        disabled={hasLiked}
                      >
                        Sarai sempre nel mio cuore ❤️
                      </button>
                    </div>
                    {annuncio.funerale && (
                      <>
                        <hr />
                        <svg xmlns="http://www.w3.org/2000/svg" height="60" width="auto" viewBox="0 0 640 512" alt="Funerale">
                        <path fill="#B197FC66" d="M344 24c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 24-32 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l32 0 0 46.4L183.3 210c-14.5 8.7-23.3 24.3-23.3 41.2L160 512l96 0 0-96c0-35.3 28.7-64 64-64s64 28.7 64 64l0 96 96 0 0-260.8c0-16.9-8.8-32.5-23.3-41.2L344 142.4 344 96l32 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-32 0 0-24zM24.9 330.3C9.5 338.8 0 354.9 0 372.4L0 464c0 26.5 21.5 48 48 48l80 0 0-238.4L24.9 330.3zM592 512c26.5 0 48-21.5 48-48l0-91.6c0-17.5-9.5-33.6-24.9-42.1L512 273.6 512 512l80 0z"/></svg>
                        <br />
                        {annuncio.funerale}
                      </>
                    )}
                    {annuncio.cimitero && (
                      <>
                        <hr />
                        <svg xmlns="http://www.w3.org/2000/svg" height="60" width="auto" viewBox="0 0 384 512">
                        <path fill="#B197FC66" d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/></svg>
                        <br />
                        {annuncio.cimitero}
                      </>
                    )}
                  </h3>
                </div>
                <div className="container-fluid plr_30">
                  <div className="row justify-content-center">
                    <div className="col-md-8 col-sm-12 mb_30">
                      <div className="white_box_tittle list_header">
                        <p className="indirizzoPubblico" style={{fontWeight: '400', lineHeight: '1.6rem'}}>{annuncio.testo}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>              
            </div>
          </React.Fragment>
        ) : null} 
        
        {allegati.length > 0 && (
          <div className="col-lg-12">
            <div className="row justify-content-center">
              <h4 className="text text-center mt_30 mb_30">Partecipazioni & Ringraziamenti</h4>
              {allegati.map(allegato => (
                <div className="col-12 mb_30" key={allegato._id}>
                  <div className="row">
                    <div className="col-md-4 col-sm-3"></div>
                    <div className="col-md-4 col-sm-6 col-xs-12 mb_30">
                      <img
                        src={`/necrologi/${allegato.id_utente}/${allegato.img}`}
                        alt="Allegato all'annuncio principale"
                        onError={(e) => { e.target.src = '/necrologi/vuoto.jpg';}}
                        className="img-fluid bordoChiaro"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="col-md-8 offset-md-2 col-sm-12 p-0">
          <div className="header_iner justify-content-center">                  
            <div className="serach_field-area text-center">
              {agenzia && ( 
                <div className="info-agenzia">
                  <h3>
                    {agenzia.url ? (
                      <a href={agenzia.url} target="_blank" rel="noopener noreferrer">
                        {agenzia.logo && (
                          <><img src={`/necrologi/${agenzia._id}/logo/${agenzia.logo}`} alt="Logo" className="img-fluid" style={{maxWidth: '120px'}} />
                          <br /></>
                        )}                                                
                          {agenzia.ragSociale}
                      </a>
                    ) : (
                      agenzia.ragSociale
                    )}
                  </h3>
                  <h4 style={{color: '#b19dc9', lineHeight: '1.6rem', fontSize: '0.9rem'}}>
                    {agenzia.comune.toUpperCase()} ({agenzia.prov.toUpperCase()})<br />
                    {agenzia.tel && <>Tel: {agenzia.tel}{" - "}</>}
                    {agenzia.cell && <>Cell: {agenzia.cell}</>}
                  </h4>
                </div>
              )}

              <hr />
              <p><i>Condividi questo annuncio</i></p>
              <div className="text-center" ref={qrCodeRef}>
                <QRCodeSVG value={url} size={192} fgColor="#b19dc9" />
              </div>
              <button className="btn btn-sm btn-primary mt-3" onClick={downloadQRCode}>
                Scarica QR Code
              </button>
              <div className="container-fluid plr_30 mt-3">
                  <div className="row justify-content-center">
                    <div className="col-md-8 col-sm-12 mb_30">
                      <div className="white_box_tittle text-center">                        
                          <a href={`https://www.addtoany.com/share#url=${encodeURIComponent(MYURL)}&amp;title=${encodeURIComponent(titoloPerCondivisione)}&amp;linkname=${encodeURIComponent(titoloPerCondivisione)}`} target="_blank" rel="noreferrer" style={{marginRight: '0px'}} title="Condividi">
                            <img src="https://static.addtoany.com/buttons/a2a.svg" width="0" height="0" style={{backgroundColor: "#4c267b"}} alt="Condividi" />
                          </a> 
                          <a href={`https://www.addtoany.com/add_to/facebook?linkurl=${encodeURIComponent(MYURL)}&amp;title=${encodeURIComponent(titoloPerCondivisione)}&amp;linkname=${encodeURIComponent(titoloPerCondivisione)}`} target="_blank" rel="noreferrer" style={{marginRight: '16px'}} className="btn btn-sm btn-primary" title="Facebook">
                            <i className="ti-facebook"></i>
                          </a>
                          {/* <a href={`https://www.addtoany.com/add_to/linkedin?linkurl=${encodeURIComponent(MYURL)}&amp;title=${encodeURIComponent(titoloPerCondivisione)}&amp;linkname=${encodeURIComponent(titoloPerCondivisione)}`} target="_blank" rel="noreferrer" style={{marginRight: '4px'}} className="btn btn-sm btn-primary" title="LinkedIn">
                            <i className="ti-linkedin"></i>
                          </a> */}
                          <a href={`https://www.addtoany.com/add_to/x?linkurl=${encodeURIComponent(MYURL)}&amp;title=${encodeURIComponent(titoloPerCondivisione)}&amp;linkname=${encodeURIComponent(titoloPerCondivisione)}`} target="_blank" rel="noreferrer" style={{marginRight: '16px'}} className="btn btn-sm btn-primary" title="X (ex Twitter)">
                            <i className="ti-twitter"></i>
                          </a>
                          <a href={`https://www.addtoany.com/add_to/whatsapp?linkurl=${encodeURIComponent(MYURL)}&amp;title=${encodeURIComponent(titoloPerCondivisione)}&amp;linkname=${encodeURIComponent(titoloPerCondivisione)}`} target="_blank" rel="noreferrer" style={{marginRight: '16px'}} className="btn btn-sm btn-primary" title="WhatsApp">
                            <i className="fab fa-whatsapp"></i>
                          </a>
                          <a href={`https://www.addtoany.com/add_to/telegram?linkurl=${encodeURIComponent(MYURL)}&amp;title=${encodeURIComponent(titoloPerCondivisione)}&amp;linkname=${encodeURIComponent(titoloPerCondivisione)}`} target="_blank" rel="noreferrer" style={{marginRight: '0px'}} className="btn btn-sm btn-primary" title="Telegram">
                            <i className="fab fa-telegram"></i>
                          </a>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
        <Footer />
      </section>
    </HelmetProvider>
  );
}

export default Necrologio;